/**
 * Where each mount portals its dialogs (kernel apps, WS-14 section 3.6; layered shell surfaces,
 * WS-8 section 3.12, D27, R-9), moved from the shell's `services/portal-target-service.ts`.
 *
 * A routed or system mount portals into its own element, the CSS scope wrapper. An embedded mount
 * portals into its own child of the host's `#zion-overlay`, which carries the mount's
 * `data-hosted-app` so the app's scoped CSS matches. The target is never `document.body` and
 * never another mount's element.
 *
 * Stacking, bottom to top: `#root` at `auto`; `#zion-overlay` at `--ds-z-overlay` (100), which
 * forms a stacking context, so an embedded dialog at `--ds-z-modal` paints at 100 against its
 * siblings; `#zion-maintenance` at `--ds-z-toast` (400), which covers it. Each host document owns the element
 * order and `#zion-maintenance`'s z-index (R-9).
 */
import { disposedError, type MountScope } from './kit.js';

/** The id of the overlay element each host's `index.html` renders (R-9). */
export const OVERLAY_ELEMENT_ID = 'zion-overlay';

/** The overlay's z-index: the design-token floor `tokens.css` names for the app-host overlay. */
export const OVERLAY_Z_INDEX = 'var(--ds-z-overlay)';

/**
 * The document's portal targets.
 *
 * - `targetFor(scope)` returns the mount's `portalTarget` function. A routed or system mount's
 *   call returns its own `request.element`. An embedded mount's first call appends
 *   `div[data-hosted-app=<appId>][data-surface-portal=<mountId>]` with `pointer-events: auto` to
 *   the overlay and every call returns that one child; the mount's end removes it. After the
 *   mount ends the function throws `disposed`.
 * - `dispose()` removes every child this module appended and leaves the overlay and its style.
 */
export interface PortalTargets {
  targetFor(scope: MountScope): () => HTMLElement;
  dispose(): void;
}

/**
 * Build the targets over the overlay element.
 *
 * @param overlay the host's `#zion-overlay`. It gets `position: fixed`, `inset: 0`,
 *   `pointer-events: none` and `z-index: var(--ds-z-overlay)`.
 * @throws TypeError when `overlay`'s id is not {@link OVERLAY_ELEMENT_ID} or it is not connected
 *   to a document.
 */
export function createPortalTargets(overlay: HTMLElement): PortalTargets {
  if (overlay.id !== OVERLAY_ELEMENT_ID) {
    throw new TypeError(`portal overlay must be #${OVERLAY_ELEMENT_ID}`);
  }
  if (!overlay.isConnected) {
    throw new TypeError(`portal overlay #${OVERLAY_ELEMENT_ID} is not connected to the document`);
  }
  overlay.style.position = 'fixed';
  overlay.style.inset = '0';
  overlay.style.pointerEvents = 'none';
  overlay.style.zIndex = OVERLAY_Z_INDEX;
  const children = new Set<HTMLElement>();

  return {
    targetFor(scope) {
      const { request } = scope;
      if (request.placement.kind !== 'embedded') {
        return () => {
          if (scope.disposed) throw disposedError('portalTarget');
          return request.element;
        };
      }
      let child: HTMLElement | null = null;
      scope.own(() => {
        if (child === null) return;
        child.remove();
        children.delete(child);
      });
      return () => {
        if (scope.disposed) throw disposedError('portalTarget');
        if (child === null) {
          child = overlay.ownerDocument.createElement('div');
          child.setAttribute('data-hosted-app', request.appId);
          child.setAttribute('data-surface-portal', request.mountId);
          child.style.pointerEvents = 'auto';
          overlay.appendChild(child);
          children.add(child);
        }
        return child;
      };
    },
    dispose() {
      for (const child of children) child.remove();
      children.clear();
    },
  };
}
